"use client"

import * as React from "react"
import { CartesianGrid, Line, LineChart, XAxis } from "recharts"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart"

const chartConfig = {
  views: {
    label: "Notatki",
  },
  created: {
    label: "Dodane",
    color: "#0084ff",
  },
  read: {
    label: "Przeczytane",
    color: "#10b981",
  },
} satisfies ChartConfig

interface NotesChartProps {
  data: {
    date: string
    created: number
    read: number
  }[]
}

export function NotesChart({ data }: NotesChartProps) {
  const total = React.useMemo(
    () => ({
      created: data.reduce((acc, curr) => acc + curr.created, 0),
      read: data.reduce((acc, curr) => acc + curr.read, 0),
    }),
    [data]
  )

  return (
    <Card className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-800 shadow-sm">
      <CardHeader className="flex flex-col items-stretch space-y-0 border-b border-gray-200 dark:border-zinc-800 p-0 sm:flex-row">
        <div className="flex flex-1 flex-col justify-center gap-1 px-6 py-5 sm:py-6">
          <CardTitle className="text-gray-900 dark:text-zinc-50">Aktywność notatek</CardTitle>
          <CardDescription className="text-gray-600 dark:text-zinc-400">
            Statystyki z ostatniego miesiąca
          </CardDescription>
        </div>
        <div className="flex">
          {(["created", "read"] as const).map((key) => {
            return (
              <div
                key={key}
                className="flex flex-1 flex-col justify-center gap-1 border-t border-gray-200 dark:border-zinc-800 px-6 py-4 text-left even:border-l sm:border-t-0 sm:border-l sm:px-8 sm:py-6"
              >
                <span className="text-gray-600 dark:text-zinc-400 text-xs flex items-center gap-1.5">
                  <div
                    className="h-2 w-2 rounded-full"
                    style={{ backgroundColor: chartConfig[key].color }}
                  />
                  {chartConfig[key].label}
                </span>
                <span className="text-lg leading-none font-bold sm:text-3xl text-gray-900 dark:text-zinc-50">
                  {total[key].toLocaleString()}
                </span>
              </div>
            )
          })}
        </div>
      </CardHeader>
      <CardContent className="px-2 sm:p-6 bg-white dark:bg-zinc-900">
        <ChartContainer
          config={chartConfig}
          className="aspect-auto h-[250px] w-full"
        >
          <LineChart
            accessibilityLayer
            data={data}
            margin={{
              left: 12,
              right: 12,
            }}
          >
            <CartesianGrid vertical={false} stroke="currentColor" className="stroke-gray-200 dark:stroke-zinc-800" />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              minTickGap={32}
              stroke="currentColor"
              className="text-gray-600 dark:text-zinc-400"
              tickFormatter={(value) => {
                const date = new Date(value)
                return date.toLocaleDateString("pl-PL", {
                  month: "short",
                  day: "numeric",
                })
              }}
            />
            <ChartTooltip
              content={
                <ChartTooltipContent
                  className="w-[150px] bg-white dark:bg-zinc-900 border-gray-200 dark:border-zinc-800"
                  labelFormatter={(value: any) => {
                    return new Date(value).toLocaleDateString("pl-PL", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                    })
                  }}
                />
              }
            />
            <Line
              dataKey="created"
              type="monotone"
              stroke={chartConfig.created.color}
              strokeWidth={2}
              dot={false}
            />
            <Line
              dataKey="read"
              type="monotone"
              stroke={chartConfig.read.color}
              strokeWidth={2}
              dot={false}
            />
          </LineChart>
        </ChartContainer>
      </CardContent>
    </Card>
  )
}
